import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, TrendingUp, Calendar, Filter, Sparkles, ArrowRight, Users, ChevronDown, ChevronUp, Bell, Briefcase, Handshake, Heart, Store, Trash2, Share2, MessageCircle, Send, Copy, Check, CheckCircle2, XCircle, Crown, Shield, UserPlus, Link2, Infinity, Edit2, Globe, Lock, Settings } from 'lucide-react';
import UpdateCard from '../components/home/UpdateCard';
import UserZonesPanel from '../components/home/UserZonesPanel';
import ZoneUpdatesSummary from '../components/home/ZoneUpdatesSummary';
import UserZonesSummary from '../components/home/UserZonesSummary';
import WelcomeHero from '../components/home/WelcomeHero';
import MerchantStoresTile from '../components/home/MerchantQuickPanel';
import MyClubCoupons from '../components/home/MyClubCoupons';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { FilteredRolodexView } from '../FilteredRolodexView';
import { ResponsiveImage } from '../components/ResponsiveImage';

/**
 * Single bento cell — button that opens a fuller view of its widget.
 */
const WidgetCard = ({
  onClick,
  icon: Icon,
  title,
  subtitle,
  count,
  gradient = 'bg-gradient-to-br from-brand-500 to-brand-600',
  blob = 'bg-brand-200/40',
  preview,
  className = '',
}) => (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ y: -2 }}
    whileTap={{ scale: 0.98 }}
    transition={{ type: 'spring', stiffness: 320, damping: 22 }}
    className={`group relative text-right overflow-hidden rounded-2xl bg-surface
      border border-line-subtle hover:border-line shadow-card hover:shadow-elevated
      transition-shadow duration-300 p-3.5 sm:p-4 flex flex-col
      focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${className}`}
  >
    <div aria-hidden="true" className={`pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 ${blob}`} />

    <div className="relative flex items-center justify-between mb-2">
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl shadow-sm text-white ${gradient}`}>
        <Icon size={18} strokeWidth={2.25} />
      </div>
      {count != null && (
        <span className="text-2xl sm:text-3xl font-bold text-ink-strong leading-none tabular-nums">
          {count}
        </span>
      )}
    </div>

    <div className="relative">
      <h3 className="text-sm sm:text-base font-bold text-ink-strong leading-tight">{title}</h3>
      {subtitle && <p className="text-2xs sm:text-xs text-ink-muted mt-0.5 leading-snug line-clamp-1">{subtitle}</p>}
    </div>

    {preview && <div className="relative mt-2">{preview}</div>}
  </motion.button>
);

const WIDGET_TITLES = {
  updates: 'עדכונים אחרונים',
  zones: 'הקהילות שלי',
  favorites: 'רשימות מועדפים',
  businesses: 'העסקים שלי',
  events: 'המפגשים שלי',
  barters: 'הברטרים שלי',
  'admin-zones': 'קהילות שאני מנהל/ת',
  stores: 'החנויות שלי',
};

/**
 * HomePageView Component
 *
 * Stunning homepage with modern design and beautiful first impression
 */
const HomePageView = ({ navigate, chatId, safeFetch }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState('all');
  const [error, setError] = useState(null);
  const [daysFilter, setDaysFilter] = useState(7);
  const [userZones, setUserZones] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [expandedWidget, setExpandedWidget] = useState(null);

  // User entities
  const [businesses, setBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [barters, setBarters] = useState([]);
  const [stores, setStores] = useState([]);
  const [adminZones, setAdminZones] = useState([]);

  // Favorites lists
  const [favoritesLists, setFavoritesLists] = useState([]);
  const [selectedFavoritesList, setSelectedFavoritesList] = useState(null);
  const [expandedFavoritesData, setExpandedFavoritesData] = useState(null);
  const [loadingFavoritesData, setLoadingFavoritesData] = useState(false);
  const [editingFavoritesList, setEditingFavoritesList] = useState(null);
  const [editListName, setEditListName] = useState('');
  const [sharingList, setSharingList] = useState(null);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // Zone invite functionality
  const [invitingZone, setInvitingZone] = useState(null);
  const [inviteUsageLimit, setInviteUsageLimit] = useState('unlimited');
  const [inviteMaxUses, setInviteMaxUses] = useState(10);
  const [generatedInviteLink, setGeneratedInviteLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Zone edit functionality
  const [editingZone, setEditingZone] = useState(null);
  const [editZoneName, setEditZoneName] = useState('');
  const [editZoneDescription, setEditZoneDescription] = useState('');
  const [editZoneIsPrivate, setEditZoneIsPrivate] = useState(false);

  // Fetch user zones
  useEffect(() => {
    const fetchUserData = async () => {
      if (!chatId || chatId === 'guest') return;

      try {
        const response = await safeFetch(
          `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-info?chat_id=${chatId}`,
          chatId
        );
        if (response.ok) {
          const data = await response.json();
          const zones = data.user_zones || [];
          setUserZones(Array.isArray(zones) ? zones : JSON.parse(zones || '[]'));
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, [chatId]);

  // Fetch user entities
  useEffect(() => {
    const fetchUserEntities = async () => {
      if (!chatId || chatId === 'guest') return;

      try {
        const entitiesRes = await safeFetch(
          `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-entities?chat_id=${chatId}`,
          chatId
        );
        const entitiesData = await entitiesRes.json();

        setBusinesses(entitiesData['businesses'] || []);
        setEvents(entitiesData['events'] || []);
        setBarters(entitiesData['barters'] || []);
        setAdminZones(entitiesData['zones'] || []);

        // Fetch store data separately
        const adminStoreIds = entitiesData['admin_stores'] || [];
        if (adminStoreIds.length > 0) {
          const fetchWithConcurrency = async (ids, concurrency = 3) => {
            const results = [];
            for (let i = 0; i < ids.length; i += concurrency) {
              const batch = ids.slice(i, i + concurrency);
              const batchResults = await Promise.all(
                batch.map(storeId =>
                  safeFetch(`https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/dev/get-store-by-id?store_id=${storeId}`, chatId)
                    .then(res => res.json())
                    .catch(err => {
                      console.error(`Failed to fetch store ${storeId}:`, err);
                      return null;
                    })
                )
              );
              results.push(...batchResults);
            }
            return results;
          };

          fetchWithConcurrency(adminStoreIds).then(storesData => {
            setStores(storesData.filter(store => store !== null));
          });
        } else {
          setStores([]);
        }
      } catch (err) {
        console.error('Error fetching user entities:', err);
      }
    };

    fetchUserEntities();
  }, [chatId]);

  // Fetch favorites lists
  const fetchFavoritesLists = async () => {
    if (!chatId || chatId === 'guest') return;

    try {
      const res = await safeFetch(
        `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-favorites-lists?chat_id=${chatId}`,
        chatId
      );
      const data = await res.json();
      const parsedLists = (data || []).map(list => ({
        ...list,
        data: typeof list.data === 'string' ? JSON.parse(list.data) : list.data
      }));
      setFavoritesLists(parsedLists);
    } catch (err) {
      console.error('Failed to fetch favorites lists:', err);
    }
  };

  useEffect(() => {
    fetchFavoritesLists();
  }, [chatId]);

  // Fetch updates on mount (only when chatId changes)
  useEffect(() => {
    if (!chatId || chatId === 'guest') {
      setLoading(false);
      return;
    }

    fetchUpdates();
  }, [chatId]);

  const fetchUpdates = async (useLoadingState = false, customDays = null) => {
    try {
      if (useLoadingState) {
        setLoadingUpdates(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const days = customDays !== null ? customDays : daysFilter;
      const response = await safeFetch(
        `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-zones-updates?chat_id=${chatId}&days=${days}`,
        chatId
      );

      if (response.ok) {
        const data = await response.json();
        setUpdates(data.updates || []);
      } else {
        throw new Error('Failed to fetch updates');
      }
    } catch (err) {
      console.error('Error fetching updates:', err);
      setError('שגיאה בטעינת העדכונים');
    } finally {
      if (useLoadingState) {
        setLoadingUpdates(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Get unique zones from updates
  const getUniqueZones = () => {
    const zoneIds = [...new Set(updates.map(u => u.zone_id))];
    return zoneIds.map(id => ({ zone_id: id }));
  };

  // Group updates by business_id and aggregate zones
  const groupUpdatesByBusiness = (updatesToGroup) => {
    const grouped = {};

    updatesToGroup.forEach(update => {
      const key = update.business_id || update.event_id;

      if (!grouped[key]) {
        grouped[key] = {
          ...update,
          zones: [update.zone_id],
          timestamps: [update.timestamp]
        };
      } else {
        if (!grouped[key].zones.includes(update.zone_id)) {
          grouped[key].zones.push(update.zone_id);
        }
        grouped[key].timestamps.push(update.timestamp);
        grouped[key].timestamp = Math.max(...grouped[key].timestamps);
      }
    });

    return Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
  };

  // Filter updates by selected zone and type
  const getFilteredUpdates = () => {
    let filteredUpdates = updates.filter(u => u.action === 'created');

    if (selectedZone !== 'all') {
      filteredUpdates = filteredUpdates.filter(u => u.zone_id === selectedZone);
    }

    if (typeFilter !== 'all') {
      filteredUpdates = filteredUpdates.filter(u => u.type === typeFilter);
    }

    return groupUpdatesByBusiness(filteredUpdates);
  };

  // Handle card click
  const handleUpdateClick = (update) => {
    if (update.type === 'event') {
      navigate(`?entity=${update.event_id}`);
    } else {
      navigate(`?entity=${update.business_id}`);
    }
  };

  const handleDeleteFavoritesList = async (listId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את רשימת המועדפים? פעולה זו אינה ניתנת לביטול.')) return;

    try {
      await safeFetch(
        'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-user-favorites-list',
        chatId,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chat_id: chatId, list_id: listId }),
        }
      );

      setFavoritesLists(prev => prev.filter(l => l.playlist_id !== listId));
    } catch (err) {
      console.error('Failed to delete favorites list:', err);
      alert('שגיאה במחיקת רשימת המועדפים');
    }
  };

  const handleEditFavoritesList = (list) => {
    setEditingFavoritesList(list);
    setEditListName(list.data?.name || '');
  };

  const handleUpdateFavoritesList = async () => {
    if (!editListName.trim()) {
      alert('נא למלא את שם הרשימה');
      return;
    }

    try {
      await safeFetch(
        'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list',
        chatId,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: chatId,
            list_id: editingFavoritesList.playlist_id,
            action: 'rename',
            new_name: editListName.trim()
          }),
        }
      );

      // Update local state
      setFavoritesLists(prev => prev.map(l =>
        l.playlist_id === editingFavoritesList.playlist_id
          ? { ...l, data: { ...l.data, name: editListName.trim() } }
          : l
      ));

      setEditingFavoritesList(null);
    } catch (err) {
      console.error('Failed to update favorites list:', err);
      alert('שגיאה בעדכון רשימת המועדפים');
    }
  };

  const handleShareFavoritesList = (list) => {
    setSharingList(list);
  };

  const handleShareOption = async (option) => {
    if (!sharingList) return;

    try {
      await safeFetch(
        'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/share-list',
        chatId,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: chatId,
            list_id: sharingList.playlist_id
          }),
        }
      );

      const shareUrl = `${window.location.origin}?view=favorites-list&list_id=${sharingList.playlist_id}`;
      const listName = sharingList.data?.name || 'רשימת מועדפים';
      const shareText = `${listName} - רשימת המועדפים שלי במקומית`;

      if (option === 'whatsapp') {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
        window.open(whatsappUrl, '_blank');
        setSharingList(null);
      } else if (option === 'telegram') {
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        window.open(telegramUrl, '_blank');
        setSharingList(null);
      } else if (option === 'copy') {
        await navigator.clipboard.writeText(shareUrl);
        setShareLinkCopied(true);
        setTimeout(() => {
          setShareLinkCopied(false);
          setSharingList(null);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to share list:', err);
      alert('שגיאה בשיתוף הרשימה');
    }
  };

  const handleRemoveFromFavoritesList = async (entityId, entityType) => {
    if (!selectedFavoritesList) return;

    try {
      await safeFetch(
        'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list',
        chatId,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: chatId,
            list_id: selectedFavoritesList.playlist_id,
            entity_type: entityType,
            entity_id: entityId,
            action: 'remove'
          }),
        }
      );

      setExpandedFavoritesData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [entityType]: prev[entityType]?.filter(item => {
            const itemId = entityType === 'groups' ? item.group_id : item.business_id;
            return itemId !== entityId;
          }) || []
        };
      });

      setSelectedFavoritesList(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            [entityType]: prev.data[entityType]?.filter(id => id !== entityId) || []
          }
        };
      });

      setFavoritesLists(prev => prev.map(list => {
        if (list.playlist_id === selectedFavoritesList.playlist_id) {
          return {
            ...list,
            data: {
              ...list.data,
              [entityType]: list.data[entityType]?.filter(id => id !== entityId) || []
            }
          };
        }
        return list;
      }));
    } catch (err) {
      console.error('Failed to remove from favorites list:', err);
      alert('שגיאה בהסרת הפריט מרשימת המועדפים');
    }
  };

  const fetchFavoritesListData = async (list) => {
    setLoadingFavoritesData(true);
    setExpandedFavoritesData(null);

    try {
      const businessIds = list.data?.businesses || [];
      const eventIds = list.data?.events || [];
      const barterIds = list.data?.barters || [];
      const groupIds = list.data?.groups || [];

      const [businessesData, eventsData, bartersData, groupsData] = await Promise.all([
        Promise.all(businessIds.map(async (id) => {
          try {
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
            return await res.json();
          } catch (err) {
            console.error(`Failed to fetch business ${id}:`, err);
            return null;
          }
        })),
        Promise.all(eventIds.map(async (id) => {
          try {
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
            return await res.json();
          } catch (err) {
            console.error(`Failed to fetch event ${id}:`, err);
            return null;
          }
        })),
        Promise.all(barterIds.map(async (id) => {
          try {
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
            return await res.json();
          } catch (err) {
            console.error(`Failed to fetch barter ${id}:`, err);
            return null;
          }
        })),
        Promise.all(groupIds.map(async (group_obj) => {
          try {
            const group_id = group_obj.group_id;
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/group-info?group_id=${group_id}&chat_id=${chatId}&zone_id=${group_obj.zone_id}`, chatId);
            return await res.json();
          } catch (err) {
            console.error(`Failed to fetch group ${group_id}:`, err);
            return null;
          }
        })),
      ]);

      setExpandedFavoritesData({
        businesses: businessesData.filter(b => b !== null),
        events: eventsData.filter(e => e !== null),
        barters: bartersData.filter(b => b !== null),
        groups: groupsData.filter(g => g !== null),
      });
    } catch (err) {
      console.error('Failed to fetch favorites list data:', err);
    } finally {
      setLoadingFavoritesData(false);
    }
  };

  // Zone invite handlers
  const handleGenerateInviteLink = async () => {
    if (inviteUsageLimit === 'limited' && (!inviteMaxUses || inviteMaxUses < 1)) {
      alert('נא להזין מספר שימושים תקין');
      return;
    }

    try {
      const response = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/generate-zone-invite', chatId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          zone_id: invitingZone.zone_id,
          max_uses: inviteUsageLimit === 'unlimited' ? null : inviteMaxUses
        }),
      });

      const data = await response.json();
      setGeneratedInviteLink(data.invite_link);
    } catch (err) {
      console.error('Failed to generate invite link:', err);
      alert('שגיאה ביצירת קישור ההזמנה');
    }
  };

  const handleCopyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedInviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('שגיאה בהעתקת הקישור');
    }
  };

  const handleCloseInviteModal = () => {
    setInvitingZone(null);
    setInviteUsageLimit('unlimited');
    setInviteMaxUses(10);
    setGeneratedInviteLink(null);
    setLinkCopied(false);
  };

  // Zone delete handler
  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקהילה? פעולה זו אינה ניתנת לביטול.')) return;

    try {
      await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-zone', chatId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, zone_id: zoneId }),
      });

      setAdminZones(prev => prev.filter(z => z.zone_id !== zoneId));
    } catch (err) {
      console.error('Failed to delete zone:', err);
      alert('שגיאה במחיקת הקהילה');
    }
  };

  // Zone edit handlers
  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setEditZoneName(zone.zone_display_name || zone.zone_id);
    setEditZoneDescription(zone.description || '');
    setEditZoneIsPrivate(zone.is_private || false);
  };

  const handleUpdateZone = async () => {
    if (!editZoneName.trim() || !editZoneDescription.trim()) {
      alert('נא למלא את כל השדות');
      return;
    }

    try {
      await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-zone', chatId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          zone_id: editingZone.zone_id,
          new_zone_name: editZoneName.trim(),
          description: editZoneDescription.trim(),
          is_private: editZoneIsPrivate
        }),
      });

      setAdminZones(prev => prev.map(z =>
        z.zone_id === editingZone.zone_id
          ? { ...z, zone_display_name: editZoneName.trim(), description: editZoneDescription.trim(), is_private: editZoneIsPrivate }
          : z
      ));
      setEditingZone(null);
    } catch (err) {
      console.error('Failed to update zone:', err);
      alert('שגיאה בעדכון הקהילה');
    }
  };

  const userHasZones = userZones && userZones.length > 0;
  const isGuest = !chatId || chatId === 'guest';

  // Loading state — on-brand spinner on the cream surface
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-pill border-4 border-brand-100" />
            <div className="absolute inset-0 rounded-pill border-4 border-transparent border-t-brand-500 animate-spin" />
          </div>
        </motion.div>
      </div>
    );
  }

  // Guest mode — warm cream welcome card on brand palette
  if (isGuest) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center px-4">
        <Container className="max-w-lg mx-auto" style={{ direction: 'rtl' }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="text-center"
          >
            <div className="relative overflow-hidden rounded-2xl bg-surface border border-line shadow-elevated px-8 py-10">
              <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-16 h-40 w-40 bg-brand-100 rounded-pill blur-3xl opacity-70" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -right-20 h-44 w-44 bg-secondary-100 rounded-pill blur-3xl opacity-60" />

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 180, damping: 18 }}
                className="relative mx-auto inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-brand-500 text-ink-inverse shadow-md ring-4 ring-brand-50 mb-6"
              >
                <Users className="w-10 h-10" strokeWidth={1.75} />
              </motion.div>

              <h1 className="relative display-2 mb-3">ברוכים הבאים למקומית</h1>
              <p className="relative text-lead text-ink-muted mb-8">
                הקהילה המקומית שלך מחכה לך — התחברו לשכנים, עסקים ומפגשים בקהילה.
              </p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="relative"
              >
                <Button
                  onClick={() => {
                    localStorage.removeItem('chat_id');
                    window.location.reload();
                  }}
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  התחברות
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </div>
    );
  }

  const uniqueZones = getUniqueZones();
  const filteredUpdates = getFilteredUpdates();
  const totalUpdates = updates.filter(u => u.action === 'created').length;

  return (
    <div className="min-h-screen bg-surface-base" style={{ direction: 'rtl' }}>
      {/* Main Content */}
      <Container className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10">
        {!userHasZones ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="surface-elevated p-10 text-center rounded-2xl"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100 mb-6">
              <MapPin className="w-10 h-10" strokeWidth={1.75} />
            </div>
            <h3 className="heading-xl mb-3">בחר את הקהילות שלך</h3>
            <p className="text-lead text-ink-muted mb-8 max-w-md mx-auto">
              הצטרף לקהילות באזורים שמעניינים אותך וקבל עדכונים על עסקים ומפגשים
            </p>
            <Button
              onClick={() => navigate('zones')}
              variant="primary"
              size="lg"
              icon={<ArrowRight />}
              iconPosition="end"
            >
              גלה קהילות
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Warm welcome hero */}
            <WelcomeHero
              userName={typeof window !== 'undefined' ? localStorage.getItem('user_name') : null}
              userPhoto={typeof window !== 'undefined' ? (localStorage.getItem('user_photo') || localStorage.getItem('profile_picture')) : null}
              zonesCount={userZones.length}
              newUpdatesCount={updates.filter(u => u.action === 'created').length}
            />

            {/* Widget grid — compact, uniform tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
              {/* Merchant tile — only rendered when the user owns stores */}
              <MerchantStoresTile
                stores={stores}
                chatId={chatId}
                safeFetch={safeFetch}
                onOpen={() => setExpandedWidget('stores')}
              />

              {/* Customers-club referral coupons tile — only if the user has any */}
              <MyClubCoupons chatId={chatId} navigate={navigate} />

              <WidgetCard
                onClick={() => setExpandedWidget('updates')}
                icon={Bell}
                title="עדכונים"
                subtitle={`ב-${daysFilter} ${daysFilter === 1 ? 'יום' : 'ימים'} האחרונים`}
                count={filteredUpdates.length}
                gradient="bg-gradient-to-br from-brand-500 to-brand-600"
                blob="bg-brand-300/40"
              />

              <WidgetCard
                onClick={() => setExpandedWidget('zones')}
                icon={Users}
                title="הקהילות שלי"
                subtitle="קהילות שאני חבר/ה בהן"
                count={userZones.length}
                gradient="bg-gradient-to-br from-warning-500 to-warning-600"
                blob="bg-warning-300/40"
              />

              <WidgetCard
                onClick={() => setExpandedWidget('favorites')}
                icon={Heart}
                title="מועדפים"
                subtitle="רשימות שמורות"
                count={favoritesLists.length}
                gradient="bg-gradient-to-br from-danger-500 to-danger-600"
                blob="bg-danger-300/40"
              />

              <WidgetCard
                onClick={() => setExpandedWidget('businesses')}
                icon={Briefcase}
                title="עסקים"
                subtitle="העסקים שלי"
                count={businesses.length}
                gradient="bg-gradient-to-br from-brand-400 to-brand-500"
                blob="bg-brand-200/40"
                preview={businesses.length > 0 ? (
                  <div className="flex items-center gap-1.5 flex-row-reverse justify-end">
                    {businesses.slice(0, 4).map((b, i) => {
                      const img = b.cover_image || b.image;
                      const name = b.name || b.business_name || '';
                      return (
                        <div
                          key={b.business_id || i}
                          className="relative w-9 h-9 rounded-lg overflow-hidden shadow-sm ring-1 ring-white/70 bg-gradient-to-br from-brand-400 to-brand-500"
                          title={name}
                        >
                          {img ? (
                            <ResponsiveImage
                              src={img}
                              alt={name}
                              fillContainer={true}
                              placeholderClassName="bg-gradient-to-br from-brand-400 to-brand-500"
                              containerClassName="absolute inset-0"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-white">
                              <Briefcase size={14} strokeWidth={2.25} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {businesses.length > 4 && (
                      <div className="w-9 h-9 rounded-lg ring-1 ring-line bg-surface-sunken text-ink-muted text-xs font-bold flex items-center justify-center">
                        +{businesses.length - 4}
                      </div>
                    )}
                  </div>
                ) : null}
              />

              <WidgetCard
                onClick={() => setExpandedWidget('events')}
                icon={Calendar}
                title="מפגשים"
                subtitle="מפגשים שיצרתי"
                count={events.length}
                gradient="bg-gradient-to-br from-info-500 to-info-600"
                blob="bg-info-300/40"
              />

              <WidgetCard
                onClick={() => setExpandedWidget('barters')}
                icon={Handshake}
                title="ברטרים"
                subtitle="הצעות החלפה"
                count={barters.length}
                gradient="bg-gradient-to-br from-success-500 to-success-600"
                blob="bg-success-300/40"
              />

              {stores.length === 0 && (
                <WidgetCard
                  onClick={() => setExpandedWidget('stores')}
                  icon={Store}
                  title="חנויות"
                  subtitle="חנויות שאני מנהל/ת"
                  count={stores.length}
                  gradient="bg-gradient-to-br from-secondary-400 to-secondary-500"
                  blob="bg-secondary-200/40"
                />
              )}
            </div>

            {/* Expanded widget modal — opens when a bento card is clicked */}
            <Modal
              isOpen={!!expandedWidget}
              onClose={() => setExpandedWidget(null)}
              title={expandedWidget ? WIDGET_TITLES[expandedWidget] : ''}
              size="2xl"
              contentClassName="px-4 sm:px-6 py-5 max-h-[75vh] overflow-y-auto"
            >
              {/* Zones Tab */}
              {expandedWidget === 'zones' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="animate-fadeIn"
                >
                  <div className="bg-white rounded-2xl shadow-lg border border-line-subtle p-6">
                    <UserZonesSummary
                      userZones={userZones}
                      chatId={chatId}
                      safeFetch={safeFetch}
                      navigate={navigate}
                    />
                  </div>
                </motion.div>
              )}

              {/* Updates Tab */}
              {expandedWidget === 'updates' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="animate-fadeIn"
                >
                  {error && (
                    <div className="bg-danger-50 border border-danger-100 text-danger-700 rounded-xl p-4 text-center mb-6">
                      {error}
                    </div>
                  )}

                  <div className="surface-card overflow-hidden">
                    <div className="p-6">
                      <div className="mb-6">
                        <h2 className="heading-md mb-1">עדכונים אחרונים</h2>
                        <span className="text-sm text-ink-muted font-medium">
                          {filteredUpdates.length} עדכונים
                        </span>
                      </div>

                      {/* Filters */}
                      <div className="space-y-3 pb-4 border-b border-line-subtle">
                        {/* Time Filter */}
                        <div>
                          <label className="text-xs font-medium text-ink-subtle mb-2 block">תקופת זמן</label>
                          <div className="flex items-center gap-2 flex-wrap">
                            {[
                              { value: 1, label: '24 שעות' },
                              { value: 3, label: '3 ימים' },
                              { value: 7, label: 'שבוע' },
                              { value: 30, label: 'חודש' }
                            ].map(({ value, label }) => (
                              <Button
                                key={value}
                                onClick={() => {
                                  if (value !== daysFilter) {
                                    setDaysFilter(value);
                                    fetchUpdates(true, value);
                                  }
                                }}
                                variant={daysFilter === value ? 'primary' : 'subtle'}
                                size="sm"
                              >
                                {label}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Zone Filter */}
                        {uniqueZones.length > 1 && (
                          <div>
                            <label className="text-xs font-medium text-ink-subtle mb-2 block">קהילה</label>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                onClick={() => setSelectedZone('all')}
                                variant={selectedZone === 'all' ? 'primary' : 'subtle'}
                                size="sm"
                              >
                                הכל
                              </Button>
                              {uniqueZones.map(({ zone_id }) => (
                                <Button
                                  key={zone_id}
                                  onClick={() => setSelectedZone(zone_id)}
                                  variant={selectedZone === zone_id ? 'primary' : 'subtle'}
                                  size="sm"
                                >
                                  {zone_id}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Type Filter */}
                        <div>
                          <label className="text-xs font-medium text-ink-subtle mb-2 block">סוג</label>
                          <div className="flex items-center gap-2 flex-wrap">
                            {[
                              { value: 'all', label: 'הכל' },
                              { value: 'business', label: 'עסקים' },
                              { value: 'event', label: 'מפגשים' },
                              { value: 'barter', label: 'בארטר' }
                            ].map(({ value, label }) => (
                              <Button
                                key={value}
                                onClick={() => setTypeFilter(value)}
                                variant={typeFilter === value ? 'primary' : 'subtle'}
                                size="sm"
                              >
                                {label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {loadingUpdates ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="relative">
                          <div className="w-12 h-12 border-4 border-line-subtle rounded-full"></div>
                          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-gray-400 rounded-full animate-spin"></div>
                        </div>
                      </div>
                    ) : updates.length === 0 ? (
                      <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-surface-muted to-info-50 rounded-2xl mb-6">
                          <Sparkles className="w-10 h-10 text-ink-subtle" strokeWidth={2} />
                        </div>
                        <h3 className="text-2xl font-bold text-ink-strong mb-3">אין עדכונים חדשים</h3>
                        <p className="text-ink-muted">נסה לשנות את תקופת הזמן למעלה</p>
                      </div>
                    ) : filteredUpdates.length > 0 ? (
                      <div className="divide-y divide-line-subtle">
                        <AnimatePresence mode="popLayout">
                          {filteredUpdates.map((update, index) => (
                            <UpdateCard
                              key={`${update.business_id || update.event_id}-${index}`}
                              update={update}
                              onClick={() => handleUpdateClick(update)}
                              zones={uniqueZones}
                            />
                          ))}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-surface-muted rounded-2xl mb-6">
                          <Filter className="w-8 h-8 text-ink-subtle" strokeWidth={2} />
                        </div>
                        <h3 className="text-xl font-bold text-ink-strong mb-2">אין תוצאות</h3>
                        <p className="text-ink-subtle">נסה לשנות את הפילטרים</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Favorites Tab */}
              {expandedWidget === 'favorites' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="animate-fadeIn"
                >
                  {favoritesLists.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                      <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
                        <Heart size={40} className="text-danger-500" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-xl font-bold text-ink-strong mb-2">אין רשימות מועדפים עדיין</h3>
                      <p className="text-sm text-ink-muted mb-6">התחל להוסיף עסקים ומפגשים לרשימות המועדפים שלך</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {favoritesLists.map(list => {
                        const businessCount = list.data?.businesses?.length || 0;
                        const eventCount = list.data?.events?.length || 0;
                        const barterCount = list.data?.barters?.length || 0;
                        const groupCount = list.data?.groups?.length || 0;
                        const totalCount = businessCount + eventCount + barterCount + groupCount;

                        return (
                          <div
                            key={list.playlist_id}
                            className="group bg-white rounded-xl p-3 shadow-sm border border-line-subtle hover:shadow-md transition-shadow duration-200 cursor-pointer"
                            onClick={() => {
                              setSelectedFavoritesList(list);
                              fetchFavoritesListData(list);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center flex-shrink-0">
                                <Heart size={18} className="text-white fill-white" strokeWidth={2} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                  <h3 className="text-sm font-bold text-ink-strong truncate flex-1 leading-tight">
                                    {list.data?.name || 'ללא שם'}
                                  </h3>
                                  <div className="flex gap-1 shrink-0 -mt-0.5">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleShareFavoritesList(list); }}
                                      className="text-ink-subtle hover:text-brand-500 hover:bg-brand-50 rounded-md w-6 h-6 inline-flex items-center justify-center transition-colors"
                                      title="שתף רשימה"
                                    >
                                      <Share2 size={13} strokeWidth={2.25} />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleEditFavoritesList(list); }}
                                      className="text-ink-subtle hover:text-info-600 hover:bg-info-50 rounded-md w-6 h-6 inline-flex items-center justify-center transition-colors"
                                      title="שנה שם רשימה"
                                    >
                                      <Edit2 size={13} strokeWidth={2.25} />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteFavoritesList(list.playlist_id); }}
                                      className="text-ink-subtle hover:text-danger-600 hover:bg-danger-50 rounded-md w-6 h-6 inline-flex items-center justify-center transition-colors"
                                      title="מחק רשימה"
                                    >
                                      <Trash2 size={13} strokeWidth={2.25} />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {businessCount > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-400/10 text-brand-500 text-2xs font-semibold rounded-full">
                                      <Briefcase size={10} />
                                      {businessCount}
                                    </span>
                                  )}
                                  {eventCount > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-info-50 text-info-700 text-2xs font-semibold rounded-full">
                                      <Calendar size={10} />
                                      {eventCount}
                                    </span>
                                  )}
                                  {barterCount > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success-50 text-success-700 text-2xs font-semibold rounded-full">
                                      <Handshake size={10} />
                                      {barterCount}
                                    </span>
                                  )}
                                  {groupCount > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 text-2xs font-semibold rounded-full">
                                      <Users size={10} />
                                      {groupCount}
                                    </span>
                                  )}
                                  <span className="text-2xs text-ink-subtle font-medium px-1">
                                    סה"כ {totalCount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Entities widgets — each is its own expanded view */}
              {expandedWidget === 'businesses' && (
                      <div>
                        {businesses.length === 0 ? (
                          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/50 flex items-center justify-center">
                              <Briefcase size={40} className="text-brand-400" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">אין עסקים עדיין</h3>
                            <p className="text-sm text-ink-muted mb-6">צור עסק חדש כדי להתחיל לנהל את הפעילות שלך</p>
                          </div>
                        ) : (
                          <FilteredRolodexView
                            items={businesses}
                            chatId={chatId}
                            fromView='my-businesses'
                          />
                        )}
                      </div>
                    )}

              {expandedWidget === 'events' && (
                      <div>
                        {events.length === 0 ? (
                          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-info-50 to-info-100/50 flex items-center justify-center">
                              <Calendar size={40} className="text-info-600" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">אין מפגשים עדיין</h3>
                            <p className="text-sm text-ink-muted mb-6">צור מפגש חדש כדי לשתף עם הקהילה</p>
                          </div>
                        ) : (
                          <FilteredRolodexView
                            items={events}
                            chatId={chatId}
                            fromView='my-businesses'
                          />
                        )}
                      </div>
                    )}

              {expandedWidget === 'barters' && (
                      <div>
                        {barters.length === 0 ? (
                          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-success-50 to-success-100/50 flex items-center justify-center">
                              <Handshake size={40} className="text-success-600" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">אין ברטרים עדיין</h3>
                            <p className="text-sm text-ink-muted mb-6">צור ברטר חדש כדי להחליף שירותים</p>
                          </div>
                        ) : (
                          <FilteredRolodexView
                            items={barters}
                            chatId={chatId}
                            fromView='my-businesses'
                          />
                        )}
                      </div>
                    )}

              {expandedWidget === 'admin-zones' && (
                      <div>
                        {adminZones.length === 0 ? (
                          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-warning-50 to-warning-100/50 flex items-center justify-center">
                              <Users size={40} className="text-warning-600" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">אין קהילות בניהול עדיין</h3>
                            <p className="text-sm text-ink-muted mb-6">צור קהילה חדשה כדי לאחד אנשים סביב נושא משותף</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {adminZones.map(zone => (
                              <div
                                key={zone.zone_id}
                                className="group relative bg-white rounded-2xl p-6 shadow-sm border border-line-subtle hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
                              >
                                {/* Action buttons */}
                                <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                                  {zone.is_private && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInvitingZone(zone);
                                      }}
                                      className="bg-success-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-success-600 transition-all transform hover:scale-110"
                                      title="הזמן משתמשים"
                                    >
                                      <UserPlus size={16} strokeWidth={2.5} />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditZone(zone);
                                    }}
                                    className="bg-info-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-info-600 transition-all transform hover:scale-110"
                                    title="ערוך קהילה"
                                  >
                                    <Edit2 size={16} strokeWidth={2.5} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteZone(zone.zone_id);
                                    }}
                                    className="bg-danger-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-danger-600 transition-all transform hover:scale-110"
                                    title="מחק קהילה"
                                  >
                                    <Trash2 size={16} strokeWidth={2.5} />
                                  </button>
                                </div>

                                <div onClick={() => navigate('zone-details', `zone=${encodeURIComponent(zone.zone_id)}`)}>
                                  <div className="flex items-start gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                      <Users size={28} className="text-white" strokeWidth={2} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className="text-lg font-bold text-ink-strong mb-1 truncate">{zone.zone_display_name || zone.zone_id}</h3>
                                      {zone.description && (
                                        <p className="text-sm text-ink-muted line-clamp-2 mb-3">{zone.description}</p>
                                      )}
                                      <div className="flex items-center gap-3 flex-wrap">
                                        {zone.is_private && (
                                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-full border border-brand-200">
                                            <Shield size={12} />
                                            פרטית
                                          </span>
                                        )}
                                        {zone.created_at && (
                                          <span className="text-xs text-ink-subtle">
                                            נוצרה {new Date(zone.created_at).toLocaleDateString('he-IL')}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

              {expandedWidget === 'stores' && (
                      <div>
                        {stores.length === 0 ? (
                          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/50 flex items-center justify-center">
                              <Store size={40} className="text-brand-600" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">אין חנויות עדיין</h3>
                            <p className="text-sm text-ink-muted mb-6">צור חנות כדי להתחיל למכור מוצרים</p>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {stores.map(store => (
                              <div
                                key={store.store_id}
                                onClick={() => window.open(`https://hamekomit.com/${store.store_slug}`, '_blank')}
                                className="group bg-white rounded-xl cursor-pointer hover:shadow-md transition-shadow shadow-sm border border-line-subtle flex items-center gap-3 p-2.5"
                              >
                                <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-brand-400 to-brand-500 shrink-0">
                                  {store.cover_image_url ? (
                                    <ResponsiveImage
                                      src={store.cover_image_url}
                                      alt={store.name}
                                      fillContainer={true}
                                      placeholderClassName="bg-gradient-to-br from-brand-400 to-brand-500"
                                      containerClassName="absolute inset-0"
                                    />
                                  ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <Store size={20} className="text-white/60" strokeWidth={1.75} />
                                    </div>
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-bold text-ink-strong line-clamp-1 group-hover:text-brand-500 transition-colors">
                                    {store.name}
                                  </h3>
                                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                    {store.is_owner ? (
                                      <span className="inline-flex items-center gap-1 text-2xs font-semibold text-secondary-600">
                                        <Crown size={11} strokeWidth={2.5} />
                                        <span>בעל החנות</span>
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-2xs font-semibold text-info-600">
                                        <Shield size={11} strokeWidth={2.5} />
                                        <span>מנהל משנה</span>
                                      </span>
                                    )}
                                    {store.status === 'open' && (
                                      <span className="inline-flex items-center gap-1 text-2xs font-semibold text-success-600">
                                        <CheckCircle2 size={11} strokeWidth={2.5} />
                                        <span>פתוח</span>
                                      </span>
                                    )}
                                    {store.status === 'closed' && (
                                      <span className="inline-flex items-center gap-1 text-2xs font-semibold text-danger-600">
                                        <XCircle size={11} strokeWidth={2.5} />
                                        <span>סגור</span>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('store-admin', `slug=${store.store_slug}`);
                                  }}
                                  className="shrink-0 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-secondary-500 to-secondary-600 text-white hover:from-secondary-600 hover:to-secondary-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400"
                                  aria-label={`ניהול ${store.name}`}
                                >
                                  <Settings size={13} strokeWidth={2.5} />
                                  <span>ניהול</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
            </Modal>
          </>
        )}
      </Container>

      {/* Favorites List Modal */}
      {selectedFavoritesList && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col">
            <div className="p-6 border-b border-line flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center flex-shrink-0">
                    <Heart size={24} className="text-white fill-white" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-ink-strong">
                      {selectedFavoritesList.data?.name || 'ללא שם'}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedFavoritesList(null);
                    setExpandedFavoritesData(null);
                  }}
                  className="text-ink-subtle hover:text-ink-muted transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingFavoritesData ? (
                <div className="flex justify-center items-center py-20">
                  <div className="w-12 h-12 border-4 border-brand-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : expandedFavoritesData ? (
                (() => {
                  const businesses = expandedFavoritesData.businesses || [];
                  const events = expandedFavoritesData.events || [];
                  const barters = expandedFavoritesData.barters || [];
                  const groups = expandedFavoritesData.groups || [];
                  const hasNoItems = businesses.length === 0 && events.length === 0 && barters.length === 0 && groups.length === 0;

                  if (hasNoItems) {
                    return (
                      <div className="text-center py-20">
                        <Heart size={48} className="text-ink-subtle mx-auto mb-4" />
                        <p className="text-ink-muted">רשימה זו ריקה</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-6">
                      {businesses.length > 0 && (
                        <div>
                          <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                            <Briefcase size={20} className="text-brand-400" />
                            עסקים ({businesses.length})
                          </h4>
                          <FilteredRolodexView
                            items={businesses}
                            chatId={chatId}
                            fromView='favorites-list'
                            onDelete={(businessId) => handleRemoveFromFavoritesList(businessId, 'businesses')}
                          />
                        </div>
                      )}

                      {events.length > 0 && (
                        <div>
                          <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                            <Calendar size={20} className="text-info-600" />
                            מפגשים ({events.length})
                          </h4>
                          <FilteredRolodexView
                            items={events}
                            chatId={chatId}
                            fromView='favorites-list'
                            onDelete={(eventId) => handleRemoveFromFavoritesList(eventId, 'events')}
                          />
                        </div>
                      )}

                      {barters.length > 0 && (
                        <div>
                          <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                            <Handshake size={20} className="text-success-600" />
                            ברטרים ({barters.length})
                          </h4>
                          <FilteredRolodexView
                            items={barters}
                            chatId={chatId}
                            fromView='favorites-list'
                            onDelete={(barterId) => handleRemoveFromFavoritesList(barterId, 'barters')}
                          />
                        </div>
                      )}

                      {groups.length > 0 && (
                        <div>
                          <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                            <Users size={20} className="text-brand-600" />
                            קבוצות ({groups.length})
                          </h4>
                          <FilteredRolodexView
                            items={groups}
                            chatId={chatId}
                            type="group"
                            fromView='favorites-list'
                            onDelete={(groupId) => handleRemoveFromFavoritesList(groupId, 'groups')}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : null}
            </div>

            <div className="p-6 border-t border-line flex-shrink-0">
              <button
                onClick={() => {
                  setSelectedFavoritesList(null);
                  setExpandedFavoritesData(null);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-brand-400 to-brand-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Zone Modal */}
      {editingZone && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-line pb-3 sm:pb-4">
                <h3 className="text-lg sm:text-2xl font-bold text-ink-strong">ערוך קהילה</h3>
                <button
                  onClick={() => setEditingZone(null)}
                  className="text-ink-subtle hover:text-ink-muted transition-colors"
                >
                  <XCircle size={22} />
                </button>
              </div>

              {/* Zone ID (Read-only) */}
              <div className="space-y-1.5 sm:space-y-2 bg-surface-muted rounded-xl p-3 sm:p-4 border border-line">
                <label className="text-xs sm:text-sm font-semibold text-ink-muted flex items-center gap-2">
                  <Lock size={12} className="sm:w-3.5 sm:h-3.5" />
                  מזהה קהילה (לא ניתן לשינוי)
                </label>
                <div className="font-mono text-ink-strong text-xs sm:text-sm bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-line break-all">
                  {editingZone.zone_id}
                </div>
                <p className="text-[10px] sm:text-xs text-ink-subtle">זהו המזהה הקבוע של הקהילה ולא ניתן לשנותו</p>
              </div>

              {/* Zone Display Name (Editable) */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-semibold text-ink-strong">שם תצוגה של הקהילה</label>
                <input
                  type="text"
                  value={editZoneName}
                  onChange={(e) => setEditZoneName(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border border-line-strong focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/20 transition-all text-ink-strong text-sm sm:text-base"
                  placeholder="שם הקהילה"
                />
                <p className="text-[10px] sm:text-xs text-ink-muted">זהו השם שיוצג למשתמשים</p>
              </div>

              {/* Description */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-semibold text-ink-strong">תיאור</label>
                <textarea
                  value={editZoneDescription}
                  onChange={(e) => setEditZoneDescription(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border border-line-strong focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/20 transition-all resize-none text-ink-strong text-sm sm:text-base"
                  rows="3"
                  placeholder="תיאור הקהילה"
                />
              </div>

              {/* Privacy Settings */}
              <div className="space-y-2 sm:space-y-3">
                <label className="text-xs sm:text-sm font-semibold text-ink-strong">הגדרות פרטיות</label>

                <button
                  type="button"
                  onClick={() => setEditZoneIsPrivate(false)}
                  className={`w-full rounded-xl p-3 sm:p-4 transition-all border-2 ${!editZoneIsPrivate
                    ? 'bg-success-50 border-success-500 shadow-md'
                    : 'bg-white border-line hover:border-line-strong'
                    }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${!editZoneIsPrivate ? 'bg-success-500' : 'bg-line'
                      }`}>
                      <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 text-right min-w-0">
                      <h4 className="font-bold text-sm sm:text-base text-ink-strong">קהילה ציבורית</h4>
                      <p className="text-xs sm:text-sm text-ink-muted truncate">כל משתמש יכול לראות את הקהילה</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setEditZoneIsPrivate(true)}
                  className={`w-full rounded-xl p-3 sm:p-4 transition-all border-2 ${editZoneIsPrivate
                    ? 'bg-brand-50 border-brand-500 shadow-md'
                    : 'bg-white border-line hover:border-line-strong'
                    }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${editZoneIsPrivate ? 'bg-brand-500' : 'bg-line'
                      }`}>
                      <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 text-right min-w-0">
                      <h4 className="font-bold text-sm sm:text-base text-ink-strong">קהילה פרטית</h4>
                      <p className="text-xs sm:text-sm text-ink-muted truncate">רק משתמשים מוזמנים יכולים לראות</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Fixed Action Buttons at Bottom */}
            <div className="border-t border-line p-3 sm:p-4 bg-white rounded-b-2xl flex-shrink-0">
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => setEditingZone(null)}
                  className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-surface-muted text-ink rounded-xl text-sm sm:text-base font-semibold hover:bg-surface-sunken transition-colors"
                >
                  ביטול
                </button>
                <button
                  onClick={handleUpdateZone}
                  className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-brand-400 to-brand-500 text-white rounded-xl text-sm sm:text-base font-semibold hover:shadow-lg transition-all"
                >
                  שמור שינויים
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {invitingZone && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-line pb-3 sm:pb-4">
                <h3 className="text-lg sm:text-2xl font-bold text-ink-strong">הזמן משתמשים לקהילה</h3>
                <button
                  onClick={handleCloseInviteModal}
                  className="text-ink-subtle hover:text-ink-muted transition-colors"
                >
                  <XCircle size={22} />
                </button>
              </div>

              {/* Zone Info */}
              <div className="bg-gradient-to-br from-brand-50 to-brand-100/50 rounded-xl p-3 sm:p-4 border border-brand-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0">
                    <Users size={20} className="sm:w-6 sm:h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base sm:text-lg text-ink-strong truncate">{invitingZone.zone_display_name || invitingZone.zone_id}</h4>
                    {invitingZone.description && (
                      <p className="text-xs sm:text-sm text-ink-muted line-clamp-2">{invitingZone.description}</p>
                    )}
                  </div>
                </div>
              </div>

              {!generatedInviteLink ? (
                <>
                  {/* Usage Limit Options */}
                  <div className="space-y-2 sm:space-y-3">
                    <label className="text-xs sm:text-sm font-semibold text-ink-strong">מגבלת שימושים</label>

                    <button
                      type="button"
                      onClick={() => setInviteUsageLimit('unlimited')}
                      className={`w-full rounded-xl p-3 sm:p-4 transition-all border-2 ${inviteUsageLimit === 'unlimited'
                        ? 'bg-info-50 border-info-500 shadow-md'
                        : 'bg-white border-line hover:border-line-strong'
                        }`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${inviteUsageLimit === 'unlimited' ? 'bg-info-500' : 'bg-line'}`}>
                          <Infinity className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 text-right min-w-0">
                          <h4 className="font-bold text-sm sm:text-base text-ink-strong">שימושים ללא הגבלה</h4>
                          <p className="text-xs sm:text-sm text-ink-muted">הקישור יהיה תקף לתמיד</p>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setInviteUsageLimit('limited')}
                      className={`w-full rounded-xl p-3 sm:p-4 transition-all border-2 ${inviteUsageLimit === 'limited'
                        ? 'bg-warning-50 border-warning-500 shadow-md'
                        : 'bg-white border-line hover:border-line-strong'
                        }`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${inviteUsageLimit === 'limited' ? 'bg-warning-500' : 'bg-line'}`}>
                          <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 text-right min-w-0">
                          <h4 className="font-bold text-sm sm:text-base text-ink-strong">מספר שימושים מוגבל</h4>
                          <p className="text-xs sm:text-sm text-ink-muted">הגדר מספר שימושים מקסימלי</p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Max Uses Input (shown when limited is selected) */}
                  {inviteUsageLimit === 'limited' && (
                    <div className="space-y-1.5 sm:space-y-2">
                      <label className="text-xs sm:text-sm font-semibold text-ink-strong">מספר שימושים מקסימלי</label>
                      <input
                        type="number"
                        min="1"
                        value={inviteMaxUses}
                        onChange={(e) => setInviteMaxUses(parseInt(e.target.value) || 1)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border border-line-strong focus:border-warning-500 focus:outline-none focus:ring-2 focus:ring-warning-500/20 transition-all text-ink-strong text-sm sm:text-base"
                        placeholder="10"
                      />
                      <p className="text-[10px] sm:text-xs text-ink-muted">הקישור יפוג לאחר מספר השימושים הזה</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Generated Link Display */}
                  <div className="space-y-2 sm:space-y-3">
                    <label className="text-xs sm:text-sm font-semibold text-ink-strong">קישור ההזמנה שלך</label>
                    <div className="relative">
                      <div className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-success-500 bg-success-50 text-ink-strong font-mono text-xs sm:text-sm break-all pr-10 sm:pr-12">
                        {generatedInviteLink}
                      </div>
                      <button
                        onClick={handleCopyInviteLink}
                        className={`absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 rounded-lg transition-all ${linkCopied
                          ? 'bg-success-600 text-white'
                          : 'bg-success-500 text-white hover:bg-success-600'
                          }`}
                        title="העתק קישור"
                      >
                        {linkCopied ? <Check size={16} className="sm:w-5 sm:h-5" strokeWidth={2.5} /> : <Copy size={16} className="sm:w-5 sm:h-5" strokeWidth={2.5} />}
                      </button>
                    </div>
                    <p className="text-[10px] sm:text-xs text-ink-muted">
                      {inviteUsageLimit === 'unlimited'
                        ? 'קישור זה תקף ללא הגבלת שימושים'
                        : `קישור זה תקף ל-${inviteMaxUses} שימושים`}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Fixed Action Buttons at Bottom */}
            <div className="border-t border-line p-3 sm:p-4 bg-white rounded-b-2xl flex-shrink-0">
              <div className="flex gap-2 sm:gap-3">
                {!generatedInviteLink ? (
                  <>
                    <button
                      onClick={handleCloseInviteModal}
                      className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-surface-muted text-ink rounded-xl text-sm sm:text-base font-semibold hover:bg-surface-sunken transition-colors"
                    >
                      ביטול
                    </button>
                    <button
                      onClick={handleGenerateInviteLink}
                      className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-xl text-sm sm:text-base font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <Link2 size={16} className="sm:w-[18px] sm:h-[18px]" strokeWidth={2.5} />
                      <span className="truncate">צור קישור</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCloseInviteModal}
                      className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-brand-400 to-brand-500 text-white rounded-xl text-sm sm:text-base font-semibold hover:shadow-lg transition-all"
                    >
                      סגור
                    </button>
                    <button
                      onClick={handleCopyInviteLink}
                      className="flex-1 px-3 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-xl text-sm sm:text-base font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <Copy size={16} className="sm:w-[18px] sm:h-[18px]" strokeWidth={2.5} />
                      <span className="truncate">העתק קישור</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Favorites List Modal */}
      {editingFavoritesList && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-line pb-4">
                <h3 className="text-2xl font-bold text-ink-strong">ערוך רשימת מועדפים</h3>
                <button
                  onClick={() => setEditingFavoritesList(null)}
                  className="text-ink-subtle hover:text-ink-muted transition-colors"
                >
                  <XCircle size={24} />
                </button>
              </div>

              {/* List Name */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-ink-strong">שם הרשימה</label>
                <input
                  type="text"
                  value={editListName}
                  onChange={(e) => setEditListName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/20 transition-all text-ink-strong"
                  placeholder="שם הרשימה"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setEditingFavoritesList(null)}
                  className="flex-1 px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                >
                  ביטול
                </button>
                <button
                  onClick={handleUpdateFavoritesList}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-400 to-brand-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  שמור שינויים
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Options Modal */}
      {sharingList && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSharingList(null)}>
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl transform transition-all duration-300 ease-out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-line-subtle">
              <div className="flex items-center gap-3">
                <Share2 size={24} className="text-info-600" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-ink-strong">שיתוף רשימה</h3>
                  <p className="text-sm text-ink-muted mt-1">{sharingList.data?.name || 'רשימת מועדפים'}</p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <button
                onClick={() => handleShareOption('whatsapp')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-success-50 to-success-100 hover:from-success-100 hover:to-success-100 rounded-xl transition-all duration-200 border border-success-100 hover:shadow-md"
              >
                <div className="w-12 h-12 rounded-full bg-success-500 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={24} className="text-white" />
                </div>
                <div className="flex-1 text-right">
                  <h4 className="font-bold text-ink-strong">שתף בווטסאפ</h4>
                  <p className="text-sm text-ink-muted">שלח את הרשימה לחברים</p>
                </div>
              </button>

              <button
                onClick={() => handleShareOption('telegram')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-info-50 to-info-100 hover:from-info-100 hover:to-info-100 rounded-xl transition-all duration-200 border border-info-100 hover:shadow-md"
              >
                <div className="w-12 h-12 rounded-full bg-info-500 flex items-center justify-center flex-shrink-0">
                  <Send size={24} className="text-white" />
                </div>
                <div className="flex-1 text-right">
                  <h4 className="font-bold text-ink-strong">שתף בטלגרם</h4>
                  <p className="text-sm text-ink-muted">שלח את הרשימה בטלגרם</p>
                </div>
              </button>

              <button
                onClick={() => handleShareOption('copy')}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 border ${shareLinkCopied
                  ? 'bg-gradient-to-r from-success-50 to-success-100 border-success-100'
                  : 'bg-gradient-to-r from-surface-muted to-surface-muted hover:from-surface-muted hover:to-surface-sunken border-line hover:shadow-md'
                  }`}
                disabled={shareLinkCopied}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${shareLinkCopied ? 'bg-success-500' : 'bg-ink-muted'
                  }`}>
                  {shareLinkCopied ? (
                    <Check size={24} className="text-white" />
                  ) : (
                    <Copy size={24} className="text-white" />
                  )}
                </div>
                <div className="flex-1 text-right">
                  <h4 className="font-bold text-ink-strong">
                    {shareLinkCopied ? 'הקישור הועתק!' : 'העתק קישור'}
                  </h4>
                  <p className="text-sm text-ink-muted">
                    {shareLinkCopied ? 'כעת תוכל להדביק אותו בכל מקום' : 'העתק את הקישור ללוח'}
                  </p>
                </div>
              </button>
            </div>

            <div className="p-4 pt-2">
              <button
                onClick={() => setSharingList(null)}
                className="w-full px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePageView;
